import React, { useState, useEffect } from 'react';
import { connect } from 'react-redux';
import localStorageHelper from '../../../../../utils/localStorageHelper';
import { v4 } from 'uuid';
import '../FilterPanel.scss';
import useMediaQuery from '../../../../../utils/useMediaQuery';
import { filterHelperMobileSelections } from '../FilterPanel.helper';
import { odPushGA4Event } from '../../../../../utils/gaFour';
import { useSelector } from 'react-redux';

const IMG_BASE_URL = 'https://static.lenskart.com/owndays/img/plp';

const FilterItems = props => {
  const {
    handleClose,
    filterData,
    categoryType,
    modifySelectedFilterList,
    selectedOptions,
    setSelectedOptions,
    showFilterMobile,
    setShowFilterMobile,
    categoryID,
    mobilePendingSelections,
    setMobilePendingSelections,
    isCategoryTab,
  } = props;

  const userInfo = useSelector(state => state.common.userInfo);

  const [userFrameSize, setUserFrameSize] = useState('');
  const [expandedSections, setExpandedSections] = useState({});
  const [hasInitializedMobileDefault, setHasInitializedMobileDefault] = useState(false);
  const isMobileSize = useMediaQuery('(max-width: 768px)');

  let FilterResult;

  if (filterData?.result) {
    FilterResult = filterData.result.filters;

    const frameSizeFilterIndex = FilterResult.findIndex(filter => filter.id === 'frame_size_id');

    if (userFrameSize && frameSizeFilterIndex > 2) {
      FilterResult.splice(2, 0, FilterResult[frameSizeFilterIndex]);
      FilterResult.splice(frameSizeFilterIndex + 1, 1);
    }
  }

  const prefSize = localStorageHelper.getItem('prefSize');

  useEffect(() => {
    if (!filterData?.result) return;

    const syncedOptions = {};

    filterData.result.filters.forEach(filter => {
      let matchKey = filter.id;
      if (filter.id === 'lenskart_price' && props.FilterDataObj.price) {
        matchKey = 'price';
      }

      const selected = props.FilterDataObj?.[matchKey] || [];

      if (selected.length > 0) {
        syncedOptions[filter.id] = selected.map(item => item.childid);
      }
    });

    setSelectedOptions(syncedOptions);
  }, [filterData, props.FilterDataObj]);

  useEffect(() => {
    if (prefSize && categoryType) {
      setUserFrameSize(prefSize[categoryType.toLowerCase()]);
    }
  }, []);

  useEffect(() => {
    if (FilterResult?.length > 0 && showFilterMobile && !hasInitializedMobileDefault) {
      const firstFilterId = FilterResult[0].id;
      setExpandedSections({ [firstFilterId]: true });
      setHasInitializedMobileDefault(true);
    }
  }, [filterData, showFilterMobile]);

  useEffect(() => {
    if (showFilterMobile && props.FilterDataObj) {
      const syncedSelections = {};

      Object.entries(props.FilterDataObj).forEach(([key, value]) => {
        if (Array.isArray(value) && value.length > 0) {
          syncedSelections[key] = value.map(item => item.childid);
        }
      });

      setMobilePendingSelections(syncedSelections);
    }
  }, [showFilterMobile]);

  useEffect(() => {
    const count = Object.entries(selectedOptions).filter(([_, v]) => v.length > 0).length;

    if (props.onFilterAppliedCountChange) {
      props.onFilterAppliedCountChange(count);
    }
  }, [selectedOptions]);

  const toggleSection = filterId => {
    if (isMobileSize) {
      setExpandedSections({ [filterId]: true });
    } else {
      setExpandedSections(prev => ({
        ...prev,
        [filterId]: !prev[filterId],
      }));
    }
  };

  const handleOptionChange = (filterId, option) => {
    const optionId = option.id;
    if (isMobileSize) {
      setMobilePendingSelections(prev => {
        const prevSelected = prev[filterId] || [];
        const exists = prevSelected.includes(optionId);

        const updated = exists
          ? prevSelected.filter(id => id !== optionId)
          : [...prevSelected, optionId];

        odPushGA4Event('filter_option_click', {
          userInfo,
          [filterId]: updated,
        });

        return {
          ...prev,
          [filterId]: updated,
        };
      });
    } else {
      setSelectedOptions(prev => {
        const safePrev = prev || {};
        const prevSelected = safePrev[filterId] || [];
        const stringOptionId = String(optionId);
        const stringPrevSelected = prevSelected.map(id => String(id));

        const isSelected = stringPrevSelected.includes(stringOptionId);
        const updated = isSelected
          ? prevSelected.filter(id => String(id) !== stringOptionId)
          : [...prevSelected, optionId];

        odPushGA4Event('filter_applied', {
          userInfo,
          [filterId]: option.title,
        });

        odPushGA4Event('filter_option_click', {
          userInfo,
          [filterId]: option.title,
        });

        return {
          ...prev,
          [filterId]: updated,
        };
      });
    }
  };

  const totalSelectedCount = Object.values(selectedOptions).reduce(
    (sum, arr) => sum + arr.length,
    0
  );

  const clearAll = () => {
    setSelectedOptions({});
    setMobilePendingSelections({});

    odPushGA4Event('clear_filter', {
      userInfo,
    });

    if (props.FilterDataObj) {
      Object.keys(props.FilterDataObj).forEach(key => {
        props.FilterDataObj[key] = [];
      });

      if (props.FilterDataObj.price) props.FilterDataObj.price = [];
      if (props.FilterDataObj.lenskart_price) props.FilterDataObj.lenskart_price = [];
    }

    handleClose();
  };

  const clearMobilePendingSelections = () => {
    setMobilePendingSelections({});

    odPushGA4Event('clear_filter', {
      userInfo,
    });
  };

  const applyMobileSelections = () => {
    filterHelperMobileSelections({
      mobilePendingSelections,
      filterResult: FilterResult,
      setSelectedOptions,
      addFilterData: props.categoryActions.addFilterData,
      categoryID,
      getCategoryData: props.categoryActions.getCategoryData,
      setShowFilterMobile,
    });

    odPushGA4Event('filter_applied', {
      userInfo,
      ...mobilePendingSelections,
    });
  };

  // Helper render checkbox option
  const renderCheckboxOption = (option, filter, isFrameColors, isFilterImage) => {
    const currentSelected = isMobileSize
      ? mobilePendingSelections[filter.id] || []
      : selectedOptions[filter.id] || [];

    const isChecked = currentSelected.map(id => String(id)).includes(String(option.id));
    const isNoImage = !option.imageUrl;

    return (
      <label
        key={option.id || v4()}
        className={`filter-panel__checkbox-item ${isFrameColors ? 'is-filter-colors' : ''}`}
      >
        <input
          checked={isChecked}
          type="checkbox"
          onChange={e => {
            e.stopPropagation();

            handleOptionChange(filter.id, option);
            if (!isMobileSize) {
              modifySelectedFilterList(option.title, option.id, filter);
            }
          }}
        />
        <span className="filter-panel__checkbox-item-flex">
          <span
            className={`filter-panel__checkbox-item-label${
              isNoImage ? ' filter-panel__checkbox-item-label--no-image' : ''
            }`}
          >
            {option.imageUrl && (
              <img
                alt={option.title}
                className={`filter-panel__checkbox-item-icon ${isChecked ? 'checked' : ''} ${
                  isFrameColors ? 'filter-panel__checkbox-item-icon-color' : ''
                }`}
                src={option.imageUrl}
              />
            )}
            {option.title}
          </span>
          <span className="filter-panel__checkbox-item-item-count">
            {isMobileSize ? option.noOfProducts : `(${option.noOfProducts})`}
          </span>
        </span>
      </label>
    );
  };

  // Helper render section title
  const renderSectionTitle = (filter, selectedCount, isExpanded, keyPrefix = '') => {
    const filterTitleMap = {
      frame_shape_id: 'Shape & Style',
      brandname_id: 'Brands & Collection',
      material_id: 'Frame Material',
      frame_color_id: 'Frame Color',
    };
    const title = filterTitleMap[filter.id] || filter.name;

    return (
      <div
        key={`${keyPrefix}${filter.id}`}
        className={`filter-panel__section-title ${isExpanded ? 'active' : ''}`}
        onClick={() => toggleSection(filter.id)}
      >
        <span>{title}</span>
        {selectedCount > 0 ? (
          <div className="filter-panel__badge-with-arrow">
            {selectedCount}
            <span className={`filter-panel__arrow ${isExpanded ? 'up' : 'down'} white`} />
          </div>
        ) : (
          <span className={`filter-panel__arrow ${isExpanded ? 'up' : 'down'} black`} />
        )}
      </div>
    );
  };

  // Desktop Layout
  const renderDesktopLayout = () => {
    return (
      <div className="filter-panel__desktop-layout">
        {FilterResult?.map(filter => {
          const isExpanded = expandedSections[filter.id];
          const selectedCount = selectedOptions[filter.id]?.length || 0;
          const isFrameColors = filter.id === 'frame_color_id';
          const isFilterImage =
            Array.isArray(filter.data?.[0]?.options) &&
            filter.data[0].options.some(
              opt => typeof opt.imageUrl === 'string' && opt.imageUrl.startsWith('http')
            );

          return (
            <div key={filter.id} className="filter-panel__category-section">
              {renderSectionTitle(filter, selectedCount, isExpanded)}

              {isExpanded && (
                <div className="filter-panel__content-wrapper">
                  {Array.isArray(filter.data) && filter.data[0]?.options?.length > 0 && (
                    <div
                      className={`filter-panel__checkbox-group ${
                        isFilterImage ? 'filter-panel__checkbox-group-img' : ''
                      }`}
                    >
                      {filter.data[0].options.map(option =>
                        renderCheckboxOption(option, filter, isFrameColors, isFilterImage)
                      )}
                    </div>
                  )}
                </div>
              )}

              <div className="filter-panel__divider" />
            </div>
          );
        })}
      </div>
    );
  };

  // Mobile Layout
  const renderMobileLayout = () => {
    return (
      <div className="filter-panel__mobile-container">
        <div className="filter-panel__mobile-wrapper">
          {/* Mobile Sidebar */}
          <div className="filter-panel__mobile-sidebar">
            {FilterResult?.map(filter => {
              const isExpanded = expandedSections[filter.id];
              const selectedCount = isMobileSize
                ? mobilePendingSelections[filter.id]?.length || 0
                : selectedOptions[filter.id]?.length || 0;

              return renderSectionTitle(filter, selectedCount, isExpanded, 'mobile-title-');
            })}
          </div>

          {/* Mobile Content */}
          <div className="filter-panel__content-wrapper">
            {FilterResult?.map(filter => {
              const isExpanded = expandedSections[filter.id];
              const isFrameColors = filter.id === 'frame_color_id';
              const isFilterImage =
                Array.isArray(filter.data?.[0]?.options) &&
                filter.data[0].options.some(
                  opt => typeof opt.imageUrl === 'string' && opt.imageUrl.startsWith('http')
                );

              if (isExpanded) {
                odPushGA4Event('filter_viewed', {
                  userInfo,
                  [filter?.id]: filter?.name,
                });
              }

              return (
                isExpanded && (
                  <div key={`mobile-content-${filter.id}`}>
                    {Array.isArray(filter.data) && filter.data[0]?.options?.length > 0 && (
                      <div
                        className={`filter-panel__checkbox-group ${
                          isFilterImage ? 'filter-panel__checkbox-group-img' : ''
                        }`}
                      >
                        {filter.data[0].options.map(option =>
                          renderCheckboxOption(option, filter, isFrameColors, isFilterImage)
                        )}
                      </div>
                    )}
                  </div>
                )
              );
            })}
          </div>
        </div>

        {/* Mobile Buttons Layout */}
        {isMobileSize && (
          <div className="filter-panel__button-layout">
            <button
              className="filter-panel__button-layout__clear"
              onClick={clearMobilePendingSelections}
            >
              <span className="filter-panel__button-layout__clear--text">Clear</span>
            </button>

            <button className="filter-panel__button-layout__apply" onClick={applyMobileSelections}>
              <span className="filter-panel__button-layout__apply--text">Apply</span>
            </button>
          </div>
        )}
      </div>
    );
  };

  // Main Header
  const renderHeader = () => {
    return (
      <>
        <div className="filter-panel__header">
          <div className="filter-panel__title">
            <span
              className="filter-panel__title--filter"
              onClick={() => {
                odPushGA4Event('filter_icon_click', {
                  userInfo,
                });
              }}
            >
              Filters
            </span>
            {totalSelectedCount > 0 && (
              <span className="filter-panel__title--count">({totalSelectedCount} selected)</span>
            )}
            <button
              className="filter-panel__title--close"
              onClick={() => {
                setMobilePendingSelections(selectedOptions);
                setShowFilterMobile(false);
                setHasInitializedMobileDefault(false);
                odPushGA4Event('filter_cross_click', {
                  userInfo,
                });
              }}
            >
              <img alt="Close" src={`${IMG_BASE_URL}/close-sp-icon.svg`} />
            </button>
          </div>
          {totalSelectedCount > 0 && (
            <button className="filter-panel__clear" onClick={clearAll}>
              Clear All
            </button>
          )}
        </div>
        <div className="filter-panel__divider--2" />
        <div className="filter-panel__divider" />
      </>
    );
  };

  return (
    <React.Fragment>
      <div className={`${showFilterMobile ? 'filter-panel--active' : 'filter-panel'}`}>
        {renderHeader()}
        {renderDesktopLayout()}
        {renderMobileLayout()}
      </div>
    </React.Fragment>
  );
};

const mapStateToProps = state => {
  return {
    dataLocale: state.locale,
    filterData: state.category.filterData,
    categoryType: state.category.categoryType,
    FilterDataObj: state.category.FilterDataObj,
  };
};

const mapDispatchToProps = () => ({});

export default connect(mapStateToProps, mapDispatchToProps)(FilterItems);
